/**
 * IncomingCallScreen — full-screen overlay that appears whenever the in-app
 * Voice Client receives (or places) a call. For inbound calls it shows a
 * screen-pop: matched customer, address, previous jobs/calls, and which
 * business line/location was called — with Answer / Decline and quick
 * shortcuts to the customer profile or a new job.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Phone, PhoneIncoming, PhoneOff, Mic, MicOff, MapPin, Briefcase, User, History, ExternalLink, Pause,
} from "lucide-react";
import { useInAppCall } from "../contexts/InAppCallContext";
import { useVoice } from "../contexts/VoiceContext";

const formatPhone = (n) => {
  if (!n) return "Unknown";
  const digits = n.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return n;
};

const InCallControls = ({ onMute, onHangup, isMuted }) => (
  <div className="flex items-center justify-center gap-4 mt-6">
    <button
      onClick={onMute}
      className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${
        isMuted ? "bg-red-500 text-white" : "bg-white/20 text-white hover:bg-white/30"
      }`}
      data-testid="incall-mute-btn"
      aria-label={isMuted ? "Unmute" : "Mute"}
    >
      {isMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
    </button>
    <button
      onClick={onHangup}
      className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center transition-all"
      data-testid="incall-hangup-btn"
      aria-label="Hang up"
    >
      <PhoneOff className="w-6 h-6 text-white" />
    </button>
  </div>
);

const IncomingCallScreen = () => {
  const { incoming, incomingInfo, outgoing, isMuted, acceptIncoming, rejectIncoming, hangup, toggleMute } = useInAppCall();
  // Dialer call (separate voice system) — offer Hold & Answer when busy
  const { activeCall: dialerCall, holdCall: holdDialerCall } = useVoice();
  const call = incoming || outgoing;
  const [accepted, setAccepted] = useState(false);
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    if (!call) { setAccepted(false); setSeconds(0); }
  }, [call]);

  useEffect(() => {
    if (!accepted) return;
    const id = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [accepted]);

  const number = useMemo(() => {
    if (!call) return "";
    if (incoming) return call.parameters?.From || "";
    if (outgoing) return call.parameters?.To || call.customParameters?.get?.("To") || "";
    return "";
  }, [call, incoming, outgoing]);

  if (!call) return null;

  const customer = incomingInfo?.customer || null;
  const displayName = customer?.name || "";
  const lineLabel = [incomingInfo?.number_label, incomingInfo?.location_name].filter(Boolean).join(" · ");
  const calledNumber = incomingInfo?.called_number || "";
  const custAddress = customer
    ? [customer.address, customer.city, customer.state].filter(Boolean).join(", ")
    : "";

  const handleAccept = () => { acceptIncoming(); setAccepted(true); };
  const handleHoldAndAccept = async () => {
    const ok = await holdDialerCall();
    if (ok) handleAccept();
  };
  const isOutgoing = !!outgoing;
  const showInCall = isOutgoing || accepted;
  const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");

  return (
    <div
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-gradient-to-b from-emerald-700 via-emerald-800 to-slate-900 px-6"
      data-testid="incoming-call-screen"
    >
      {/* Which line was called */}
      {incoming && (lineLabel || calledNumber) && (
        <div className="absolute top-6 left-0 right-0 flex justify-center" data-testid="incoming-line-info">
          <div className="bg-white/10 backdrop-blur rounded-full px-4 py-1.5 text-white/90 text-xs flex items-center gap-2">
            <PhoneIncoming className="w-3.5 h-3.5" />
            {lineLabel ? `${lineLabel} — ` : ""}{formatPhone(calledNumber)}
          </div>
        </div>
      )}

      <div className="w-20 h-20 rounded-full bg-white/15 flex items-center justify-center mb-5">
        {showInCall ? <Phone className="w-9 h-9 text-white" /> : <PhoneIncoming className="w-9 h-9 text-white animate-pulse" />}
      </div>

      <p className="text-white/70 text-sm uppercase tracking-widest mb-2">
        {showInCall ? (isOutgoing ? "Calling…" : `In call · ${mm}:${ss}`) : "Incoming call"}
      </p>
      <p className="text-white text-3xl font-semibold leading-tight text-center" data-testid="incoming-call-name">
        {displayName || formatPhone(number)}
      </p>
      {displayName && (
        <p className="text-white/70 text-sm mt-1" data-testid="incoming-call-number">{formatPhone(number)}</p>
      )}

      {/* Screen-pop: customer intel */}
      {incoming && !showInCall && (
        <div className="mt-4 w-full max-w-sm space-y-1.5" data-testid="incoming-call-info">
          {custAddress && (
            <div className="flex items-center gap-2 text-white/80 text-sm justify-center">
              <MapPin className="w-3.5 h-3.5 shrink-0" /><span className="truncate">{custAddress}</span>
            </div>
          )}
          {customer && (
            <div className="flex items-center gap-4 justify-center text-white/70 text-xs">
              <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" /> {incomingInfo.previous_jobs} job{incomingInfo.previous_jobs === 1 ? "" : "s"}</span>
              <span className="flex items-center gap-1"><History className="w-3 h-3" /> {incomingInfo.previous_calls} previous call{incomingInfo.previous_calls === 1 ? "" : "s"}</span>
            </div>
          )}
          {customer && incomingInfo.last_job && (
            <p className="text-white/60 text-xs text-center truncate">
              Last job: {incomingInfo.last_job.title || incomingInfo.last_job.service_name} ({incomingInfo.last_job.status})
            </p>
          )}
          {!customer && (
            <p className="text-white/60 text-xs text-center">No matching customer — you can create one after answering</p>
          )}
        </div>
      )}

      {!showInCall ? (
        <>
          <div className="flex items-center justify-center gap-8 mt-8">
            <div className="flex flex-col items-center gap-2">
              <button
                onClick={rejectIncoming}
                className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center transition-all"
                data-testid="incoming-decline-btn"
                aria-label="Decline"
              >
                <PhoneOff className="w-6 h-6 text-white" />
              </button>
              <span className="text-white/70 text-xs">Decline</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              {dialerCall ? (
                <>
                  <button
                    onClick={handleHoldAndAccept}
                    className="w-16 h-16 rounded-full bg-amber-400 hover:bg-amber-300 flex items-center justify-center transition-all animate-bounce"
                    data-testid="incoming-hold-accept-btn"
                    aria-label="Hold current call and answer"
                  >
                    <Pause className="w-6 h-6 text-amber-950" />
                  </button>
                  <span className="text-white/70 text-xs text-center">Hold current<br />&amp; Answer</span>
                </>
              ) : (
                <>
                  <button
                    onClick={handleAccept}
                    className="w-16 h-16 rounded-full bg-emerald-400 hover:bg-emerald-300 flex items-center justify-center transition-all animate-bounce"
                    data-testid="incoming-accept-btn"
                    aria-label="Answer"
                  >
                    <Phone className="w-6 h-6 text-emerald-950" />
                  </button>
                  <span className="text-white/70 text-xs">Answer</span>
                </>
              )}
            </div>
          </div>

          {/* Shortcuts */}
          <div className="flex items-center gap-3 mt-8">
            {customer ? (
              <button
                onClick={() => window.open(`/dashboard/customers?open=${customer.customer_id}`, "_blank")}
                className="flex items-center gap-1.5 text-white/80 hover:text-white text-xs bg-white/10 rounded-full px-3 py-1.5 transition-colors"
                data-testid="incoming-open-customer-btn"
              >
                <User className="w-3.5 h-3.5" /> Open customer <ExternalLink className="w-3 h-3" />
              </button>
            ) : null}
            <button
              onClick={() => window.open("/dashboard/jobs/new", "_blank")}
              className="flex items-center gap-1.5 text-white/80 hover:text-white text-xs bg-white/10 rounded-full px-3 py-1.5 transition-colors"
              data-testid="incoming-create-job-btn"
            >
              <Briefcase className="w-3.5 h-3.5" /> Create job <ExternalLink className="w-3 h-3" />
            </button>
          </div>
        </>
      ) : (
        <InCallControls onMute={toggleMute} onHangup={hangup} isMuted={isMuted} />
      )}
    </div>
  );
};

export default IncomingCallScreen;
